/* External dependencies */
import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { Link } from 'react-router-dom';
import { Dropdown, Image, Spinner } from 'react-bootstrap';
import get from 'lodash/get';
import throttle from 'lodash/throttle';
import pullAllWith from 'lodash/pullAllWith';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import numbro from 'numbro';

/* Internal dependencies */
import Icon, { Icons } from '../icon/Icon';
import { ApplicationState } from '../store';
import { getCurrentUser } from '../store/ducks/currentUser';
import { getCurrentProfile, updateCurrentProfile } from '../store/ducks/currentProfile';
import { listNotificationsForUser } from 'src/api/notifications';
import { ProfileType } from 'src/types/Profile';
import FlatList from 'src/flatList/FlatList';
import MediaObject from 'src/mediaObject/MediaObject';
import ProfileImage from 'src/profiles/ProfileImage';
import { capitalizeFirstLetter } from 'src/store/helpers/users';
import Verified from 'src/verified/Verified';
import Colors from 'src/colors';
import './NotificationsButton.scss';
import RespondToHostInvitationButton from 'src/communities/manage/RespondToHostInvitationButton';
import { ParticipantStatus } from 'src/types/Participant';
import FollowUserButton from 'src/user/FollowUserButton';
import { itemToUrlPath, uriToUrlPath } from 'src/common/helpers/url';
import { getEvent } from 'src/store/ducks/events';
import { getUser } from 'src/store/ducks/users';
import { getArtist } from 'src/store/ducks/artists';
import { getCommunity } from 'src/store/ducks/communities';
import { getCollection } from 'src/store/ducks/collections';

const NotificationsButton: React.FC = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);
  const currentUserId = get(currentUser, 'id');
  const currentProfile = useSelector(getCurrentProfile);
  const currentProfileId = get(currentProfile, 'id');
  const profile = currentProfile || currentUser;
  const currentUserNotifications = get(profile, 'notifications.items', get(profile, 'notifications', []));

  const [notifications, setNotifications] = useState(currentUserNotifications);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const load = async () => {

      const currentProfileType = get(currentProfile, 'type');
      if (!profile) return;
  
      try {
        setLoading(true);
        const notificationsResponse = await listNotificationsForUser(currentUserId!, {
          limit: 15,
          profileId: currentProfileType === 'user' ? undefined : get(currentProfile, 'id'),
        });
        const fetchedNotifications = get(notificationsResponse, 'items', []);
  
        if (fetchedNotifications) {
          setNotifications(fetchedNotifications);
          dispatch(updateCurrentProfile({
            ...profile,
            notifications: currentProfileType === 'user' ? fetchedNotifications : notificationsResponse,
          } as ProfileType));
        }
      } catch (e) {
        console.error('load error: ', e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUserId, currentProfileId]);

  const refreshNotifications = useCallback(async () => {
    const currentUserId = get(currentUser, 'id');
    const currentProfileType = get(currentProfile, 'type');
    if (!profile) return;

    try {
      setRefreshing(true);
      const notificationsResponse = await listNotificationsForUser(currentUserId!, {
        limit: 15,
        profileId: currentProfileType === 'user' ? undefined : get(currentProfile, 'id'),
      });
      const fetchedNotifications = get(notificationsResponse, 'items', []);

      if (fetchedNotifications?.length) {
        setNotifications(fetchedNotifications);
        dispatch(updateCurrentProfile({
          ...profile,
          notifications: currentProfileType === 'user' ? fetchedNotifications : notificationsResponse,
        } as ProfileType));
      }
    } catch (e) {
      console.error('refreshNotifications error:', e);
    } finally {
      setRefreshing(false);
    }
  }, [currentUser, currentProfile, dispatch]);

  const debouncedRefresh = useCallback(throttle(refreshNotifications, 60000), [refreshNotifications]);

  const getSections = useCallback(() => {
    const now = moment();
    const byDate = (createdAt: string) => moment(createdAt);
    const notificationsCopy = [...notifications];

    const filterBy = (predicate: (m: moment.Moment) => boolean, exclude: any[]) =>
      pullAllWith(notificationsCopy.filter(({ createdAt }) => predicate(byDate(createdAt))), exclude, isEqual);

    const newN = filterBy(m => m.isSame(now, 'hour'), []);
    const today = filterBy(m => m.isSame(now, 'day') && !m.isSame(now, 'hour'), newN);
    const yesterday = filterBy(m => m.isSame(moment().subtract(1, 'day'), 'day'), [...newN, ...today]);
    const week = filterBy(m => m.isSame(now, 'week') && !today.includes(m) && !yesterday.includes(m), [...newN, ...today, ...yesterday]);
    const month = filterBy(m => m.isSame(now, 'month') && !week.includes(m), [...newN, ...today, ...yesterday, ...week]);
    const earlier = pullAllWith(notificationsCopy, [...newN, ...today, ...yesterday, ...week, ...month], isEqual);

    return [
      { title: 'New', items: newN },
      { title: 'Today', items: today },
      { title: 'Yesterday', items: yesterday },
      { title: 'This week', items: week },
      { title: 'This month', items: month },
      { title: 'Earlier', items: earlier },
    ].filter(({ items }) => items.length);
  }, [notifications]);

  const renderRight = (notification: any): React.ReactNode | undefined => {
    let { items = {}, deepLinkUrl } = notification;
    items = get(items, 'items', []);

    const [item] = items;
    const deepUrl = uriToUrlPath(deepLinkUrl)!;
    const itemUrl = itemToUrlPath(item)!;

    return (
      <Link
        to={deepUrl || itemUrl}
        className="btn m-0 p-0"
        style={{ maxHeight: 'none', maxWidth: 'none'}}
        onClick={() => setShowDropdown(false)}
      >
        <NotificationRightWrapped
          notification={notification}
          notificationItem={item}
        />
      </Link>
    );
  };

  const renderSection = useCallback(({ item: section }: any) => {
    const title = get(section, 'title');
    const items = get(section, 'items', []);
    return (
      <div className="mb-3">
        <h5 className="text-bold mb-4">{title}</h5>
        <FlatList
          data={items}
          renderItem={({ item: notification }) => {
            let { action, users = {}, items = {}, deepLinkUrl, createdAt } = notification;
            items = get(items, 'items', []);
            const actors = get(users, 'items', []); // This may include other profile types besides users
            const actorsTotal = get(users, 'total', actors.length);
            const [item] = items;
            const itemId = get(item, 'id');

            const [actor, actor2] = actors;
            // Actor 1
            const actorId = get(actor, 'id');
            const actorName = get(actor, 'name');
            const actorType = get(actor, 'type');

            // Actor 2
            const actor2Id = get(actor2, 'id');
            const actor2Name = get(actor2, 'name');
            const actor2Type = get(actor2, 'type');
            const itemType = get(item, 'type');
            const deepUrl = uriToUrlPath(deepLinkUrl)!;
            const itemUrl = itemToUrlPath(item)!;

            return (
              <MediaObject
                className="mb-4"
                text={(
                  <div className="d-flex align-items-center m-0 p-0 mb-1" style={{ maxWidth: '90%' }}>
                    <Link to={itemToUrlPath(actor)!} className="d-flex align-items-center mr-1 text-white m-0 p-0">
                      <h5 className="text-bold m-0 p-0">{capitalizeFirstLetter(actorName)}</h5>
                      {Boolean(actors.length !== 2) && (
                        <Verified
                          profile={actor}
                          size={16}
                        />
                      )}
                    </Link>
                    {Boolean(actors.length > 1) && <h5 className="text-white">and </h5>}
                    {Boolean(actors.length === 2) && (
                      <Link to={itemToUrlPath(actor2)!} className="d-flex align-items-center mr-1 text-white m-0 p-0">
                        <h5 className="text-bold m-0 p-0">{capitalizeFirstLetter(actor2Name)}</h5>
                      </Link>
                    )}
                    {Boolean(actors.length > 2 && actorsTotal > 2) && (
                      <h5 className="text-white text-bold m-0 p-0">{numbro(actorsTotal - 1).format({ thousandSeparated: true })} others</h5>
                    )}
                    <h5 className="text-white m-0 p-0" style={{ fontWeight: 400, color: 'rgba(255,255,255,0.75)'}}>{action}</h5>
                  </div>
                )}
                textSize="h5"
                subtext={createdAt && moment(createdAt).fromNow()}
                subtextSize="h6"
                image={actors.length === 0 ? undefined : actors.length === 1 ? (
                  <ProfileImage profile={actor} imageSize={50} />
                ) : (
                  <div style={{ width: '100%' }}>
                    {actors.slice(0, 2).map((profile: ProfileType, index: number) => {
                      const { id, images = [] } = profile;
                      const [image] = images;

                      if (!image) return null;

                      return (
                        <div key={id} style={{ position: 'absolute', bottom: 0, left: index * 6 }}>
                          <ProfileImage
                            profile={profile}
                            imageSize={75}
                            style={{
                              width: 75,
                              height: undefined,
                              aspectRatio: 1,
                              borderRadius: 37.5,
                              borderWidth: 3,
                              borderColor: Colors.background,
                            }}
                            // blurRadius={25}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                right={renderRight(notification)}
                imageSize="12.5%"
                wrap={false}
              />
            );
          }}
        />
      </div>
    );
  }, []);

  const sections = getSections();
  const profileType = get(profile, 'type');

  return (
    <Dropdown align="end" show={showDropdown} onToggle={setShowDropdown}>
      <Dropdown.Toggle variant="default" className="btn bg-secondaryButton" style={{ aspectRatio: '1', borderRadius: 100 }}>
        <Icon name={Icons.bell} className="bm-Icon--white" size={22} />
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-secondaryButton text-white p-4" style={{ minWidth: 320, maxHeight: 500, overflowY: 'auto' }}>
        {Boolean(!loading && !sections.length) && (
          <div className="d-flex flex-column justify-content-center align-items-center">
            <div
              className="d-flex flex-column justify-content-center align-items-center mb-3"
              style={{ backgroundColor: 'rgba(0,0,0,0.75)', width: 75, aspectRatio: '1', borderRadius: 37.5 }}
            >
              <Icon name={Icons.bell} className="bm-Icon--primary" size={40} />
            </div>
            <h6 className="text-center text-white">No notifications yet</h6>
            <p className="bm-Text--secondaryText text-center mb-4">
              {profileType === 'user' ? 'Invite your friends to see their updates!' : 'Your notifications will appear here.'}
            </p>
          </div>
        )}
        {Boolean(sections.length) && (
          <FlatList
            data={sections}
            renderItem={renderSection}
          />
        )}
        {Boolean(loading) && (
          <div className="d-flex justify-content-center align-items-center"><Spinner size="sm" /></div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const NotificationRight: React.FC<any> = ({ notification, notificationItem }) => {
  let { notificationType, users = {}, params = {} } = notification;
  users = get(users, 'items', []);

  const [user] = users;
  const itemImageUrl = get(notificationItem, 'images[0].url');

  console.log('notificationType', notificationType);

  if (notificationType === 'friendRequestSent') {
    return (
      <FollowUserButton
        user={user}
        fontSize="1rem"
      />
    );
  } else if (notificationType === 'followSent' || notificationType === 'followRequestSent') {
    return (
      <FollowUserButton
        user={user}
        fontSize="1rem"
      />
    );
  } else if (notificationType === 'cohostRequestSent') {
    const creator = get(params, 'creator');
    const eventCreatorStatus = get(notificationItem, 'creatorStatus');
    const isInvited = Boolean(eventCreatorStatus === ParticipantStatus.invited);

    if (isInvited) {
      return (
        <RespondToHostInvitationButton
          creator={creator}
          event={notificationItem}
          fontSize="1rem"
          showIcon={false}
        />
      );
    }
  } else if (notificationType === 'teamMemberRequestSent' || notificationType === 'ownershipTransferRequestSent') {
    const creator = get(params, 'creator');
    const communityCreatorStatus = get(notificationItem, 'creatorStatus');
    const isInvited = Boolean(communityCreatorStatus === ParticipantStatus.invited);

    return null;

    // if (isInvited) {
    //   return (
    //     <RespondToCreatorInvitationButton
    //       creator={creator}
    //       profile={notificationItem}
    //       textSize="h6"
    //       showIcon={false}
    //     />
    //   );
    // }
  }

  if (Boolean(itemImageUrl)) {
    return (
      <Image
        src={itemImageUrl}
        style={{ width: 40, height: 40, borderRadius: 5 }}
      />
    );
  }
  
  return null;
};

const mapStateToPropsNotificationsRight = (state: ApplicationState, { notificationItem }: any) => {
  const itemId = get(notificationItem, 'id');
  const itemType = get(notificationItem, 'type');
  let getter;

  if (itemType === 'event') {
    getter = getEvent;
  } else if (itemType === 'user') {
    getter = getUser;
  } else if (itemType === 'artist') {
    getter = getArtist;
  } else if (itemType === 'community') {
    getter = getCommunity;
  } else if (itemType === 'collection') {
    getter = getCollection;
  }

  return {
    notificationItem: getter ? { ...notificationItem, ...getter(state, itemId) } || notificationItem : notificationItem,
  };
};

const NotificationRightWrapped = connect(mapStateToPropsNotificationsRight)(NotificationRight);

const mapStateToProps = (state: ApplicationState) => ({
  currentUser: getCurrentUser(state),
  currentProfile: getCurrentProfile(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCurrentProfile: (profile: ProfileType) => { dispatch(updateCurrentProfile(profile)); },
  // setNotificationsLastdived: (notificationsLastdived: Date) => {
  //   dispatch(setNotificationsLastdived(notificationsLastdived));
  // },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter<any, any>(NotificationsButton));